import { supabase } from "../supabase/SupabaseClient";
import { traerTodas } from "./paginar";
import { preciosDeclarados } from "./ypf";
import { traerAsignaciones } from "./asignacion";
import { furgonesRTO, tractoresRTO } from "./porteria";
import { FLOTABOT } from "../components/layout/secciones";
import { abrirSesionEnFlotaBot } from "./ssoFlotaBot";

/* De dónde leen las fórmulas HUB.… de FiboSheets (ver `shared/planillaHub`).
 *
 * ── CON LA SESIÓN DE QUIEN MIRA, Y NADA MÁS ────────────────────────────────
 *
 * Todo va por el cliente de siempre, con la sesión del que tiene la planilla
 * abierta: la RLS decide qué vuelve. Ninguna llave de servicio, ninguna
 * función `security definer` nueva, ninguna tabla que el hub no leyera ya.
 * Cada lectura es la misma que hace la pantalla dueña de ese dato (YPF,
 * Presidencia, Urea, los padrones), y donde ya había una función se usa ésa.
 *
 * ── LO QUE NO ESTÁ, Y POR QUÉ ──────────────────────────────────────────────
 *
 * Kilómetros por camión: `km_gps_diario` tiene RLS sin políticas (la lee sólo
 * el bot) y abrirla es una migración. Teléfonos y domicilios: no hacen falta
 * para cuentas de operación y no se ponen a un `=` de distancia.
 *
 * Multas por DNI: la pantalla las consulta EN VIVO contra los sitios de PBA,
 * CABA y SINAI (`consultar-multas-persona`) y no las guarda. Una fórmula que
 * pegara afuera en cada recálculo sería una consulta a terceros por tecla, así
 * que HUB.MULTAS va por interno o patente, sobre lo guardado. La LINTI no se
 * carga en ningún lado del hub: HUB.DOC_VENCE no la ofrece.
 *
 * Un error se TIRA, no vuelve vacío: «0 viajes» con la base caída es un
 * número falso; el caché lo convierte en un #HUB que dice qué falló.
 *
 * LA ÚNICA QUE NO VA A LA BASE: la ubicación de las unidades (HUB.UBICACION)
 * vive en FlotaBot, que la junta del satelital. Va por el mismo puente de
 * sesión que la herramienta de Arribos (`services/satelitalPosiciones`), con
 * la cookie de FlotaBot de quien mira, y FlotaBot pide la misma llave que
 * para esa herramienta. Ver `ubicaciones` al final. */

/* Mis permisos, para que la fórmula sepa decir «no tenés acceso» en vez de
   un 0. Es la misma función de la base que arma el menú, pero acá un error
   SÍ se tira: tragado, se leería como «no tenés ningún permiso». */
export async function permisos() {
  const { data, error } = await supabase.rpc("mis_permisos");
  if (error) throw error;
  return Array.isArray(data) ? data : [];
}

/* Los precios del D-500 que declaró Carlos Becerra: la misma lectura que la
   pantalla de YPF. Cuál rige en cada momento lo decide `precioDeclarado`. */
export const preciosGasoil = () => preciosDeclarados();

/* El padrón de la flota, tractores y furgones, con lo justo para contestar
   patente, tipo, estado, marca y modelo. Son ~650 + ~680 filas: se traen una
   vez por planilla y cada celda busca la suya adentro, en vez de un pedido
   por celda. */
const COLS_TRACTOR = "id, interno, dominio, marca, modelo, estado, estado_calculado, estado_manual";
const COLS_FURGON = "id, interno, dominio, marca, modelo, estado, tipo_acoplado";
export async function flota() {
  const [tractores, furgones] = await Promise.all([
    traerTodas(() => supabase.from("tractores").select(COLS_TRACTOR, { count: "exact" }).order("id")),
    traerTodas(() => supabase.from("furgones").select(COLS_FURGON, { count: "exact" }).order("id")),
  ]);
  return { tractores, furgones };
}

/* El padrón de personal, sin teléfono ni domicilio. Mismo criterio que la
   flota: una vez, y se busca adentro. */
const COLS_PERSONA = "id, dni, apellido, nombres, nombre, legajo, puesto, especializacion, estado";
export function personas() {
  return traerTodas(() => supabase.from("personas").select(COLS_PERSONA, { count: "exact" }).order("id"));
}

/* Qué tractor tiene asignado cada chofer (`chofer_tractor`, con su RLS de
   `ver.flota` o `ver.gente`). */
export const asignaciones = () => traerAsignaciones();

/* Un texto para `ilike` que sea literal: el % y el _ de la base y el * de
   PostgREST dejan de ser comodines. «BGH» es BGH, no «todo lo que empiece…». */
const literal = (s) => String(s).replace(/\*/g, "").replace(/[\\%_]/g, (c) => `\\${c}`);

/* Cuántos viajes, contados por la base: la vista `presidencia_viajes` (la del
   tablero de Presidencia, sin anulados, con `fecha` = carga o salida y el
   cliente ya normalizado) y sólo el total, sin traer filas. Sin
   `ver.presidencia` la vista da cero filas: por eso la fórmula pregunta el
   permiso antes de mostrar un número.

   SIN LOS TRAMOS CORTOS Río Grande ↔ Río Gallegos (05/10/2026): no cuentan
   como viaje, ni en el resumen del bot ni en el tablero, y una planilla que
   los sumara daría un tercer número para la misma semana. */
export async function viajes({ desde, hasta, sentido = "", cliente = "" }) {
  let q = supabase
    .from("presidencia_viajes")
    .select("id", { count: "exact", head: true })
    .gte("fecha", desde)
    .lte("fecha", hasta)
    .eq("tramo_corto", false);
  if (sentido) q = q.eq("sentido", sentido);
  if (cliente) q = q.ilike("cliente", literal(cliente));
  const { count, error } = await q;
  if (error) throw error;
  if (typeof count !== "number") throw new Error("la base no devolvió el total");
  return count;
}

/* Los mismos viajes, FILA POR FILA, para «Insertar tabla del hub»
   (`shared/tablasHub`): la misma vista, el mismo «sin tramos cortos» y el
   mismo sentido que `viajes`, para que la tabla volcada y =HUB.VIAJES del
   mismo período den el mismo número. Sin cliente: la tabla ya lo trae en su
   columna y se filtra en la hoja. Ordenado por fecha e id (único), como pide
   `traerTodas`. */
const COLS_VIAJE = "id, fecha, sentido, origen, destino, cliente, tipo, tipo_acoplado, bitren, fletero, empresa";
export function listaViajes({ desde, hasta, sentido = "" }) {
  return traerTodas(() => {
    let q = supabase
      .from("presidencia_viajes")
      .select(COLS_VIAJE, { count: "exact" })
      .gte("fecha", desde)
      .lte("fecha", hasta)
      .eq("tramo_corto", false);
    if (sentido) q = q.eq("sentido", sentido);
    return q.order("fecha", { ascending: true }).order("id", { ascending: true });
  });
}

/* Un día ARGENTINO empieza a las 00:00 de acá: `fecha` es timestamptz, y
   cortar por el string AAAA-MM-DD correría el día tres horas (mismo cuidado
   que `services/urea.js`). */
const corteUTC = (dia, diasDespues = 0) => {
  const d = new Date(`${dia}T00:00:00-03:00`);
  d.setDate(d.getDate() + diasDespues);
  return d.toISOString();
};

/* Las cargas de urea entre dos días (inclusive), con lo justo para sumar y
   para listarlas (la tabla del hub muestra también patente y vale). */
export function urea({ desde, hasta }) {
  return traerTodas(() =>
    supabase
      .from("urea_cargas")
      .select("id, fecha, interno, patente, litros, vale", { count: "exact" })
      .gte("fecha", corteUTC(desde))
      .lt("fecha", corteUTC(hasta, 1))
      .order("fecha", { ascending: true })
      .order("id", { ascending: true }),
  );
}

/* El RTO de una flota entera: `rto_tractores` o `rto_furgones`, las mismas
   funciones que las dos pantallas de RTO, con el veredicto ya armado por la
   base. Se pide por flota y no por unidad: una columna de internos es UN
   pedido. */
export const rto = (tipo) => (tipo === "furgon" ? furgonesRTO() : tractoresRTO());

/* Las multas guardadas (la tabla de la pantalla Multas) y qué patentes se
   consultaron alguna vez: sin lo segundo, «0 multas» de una patente que nadie
   miró sería un número inventado. Son ~1.000 filas cada una: enteras, una vez
   por planilla. Las dos con la RLS de `ver.documentos`. Acta, jurisdicción,
   lugar y descripción viajan para la tabla de multas impagas
   (`shared/tablasHub`): la misma lectura, no una segunda. */
export async function multas() {
  const [actas, consultas] = await Promise.all([
    traerTodas(() =>
      supabase
        .from("multas")
        .select("id, dominio, jurisdiccion, acta, ocurrida_en, lugar, descripcion, monto, pagada", { count: "exact" })
        .order("id"),
    ),
    traerTodas(() =>
      supabase.from("multas_consulta").select("dominio, consultada_en", { count: "exact" }).order("dominio"),
    ),
  ]);
  return { actas, consultas };
}

/* Los vencimientos de los choferes (`chofer_documentos`, la tabla de
   Documentos), SIN teléfono, dirección ni anotaciones: para una fecha no
   hacen falta. */
export function documentos() {
  return traerTodas(() =>
    supabase
      .from("chofer_documentos")
      .select("dni, nombre, registro_vence, psicofisico_vence, peligrosas_vence, requiere_peligrosas, activo", { count: "exact" })
      .order("dni"),
  );
}

/* Los MIC y los CRT de aduana, con las columnas que contestan las fórmulas.
   Son decenas: enteros y una vez. Las partes del CRT viajan porque
   «remitente» y «destinatario» salen de ahí (las firma `crt_emitir`).
   `estado` viaja también para desempatar: desde el 08/10 un CRT anulado deja
   su número libre y el mismo número puede estar en un anulado y en un vivo, y
   desde el 16/10 un borrador puede tener el de un emitido (`buscarNumero`,
   en shared/planillaHub, se queda con el emitido; si no hay, con el vivo). */
const COLS_MIC =
  "id, numero, estado, anulado_en, hoja, hojas, fecha_emision, crt_id, placa, acoplado_placa, " +
  "motorista, peso_bruto, bultos, aduana_partida, aduana_destino";
const COLS_CRT =
  "id, numero, estado, anulado_en, emitido_en, carga_fecha, carga_lugar, entrega_lugar, " +
  "remitente, destinatario, mercaderia, peso_bruto, bultos, valor_monto, valor_moneda, camiones";
export function mic() {
  return traerTodas(() => supabase.from("mic").select(COLS_MIC, { count: "exact" }).order("id"));
}
export function crt() {
  return traerTodas(() => supabase.from("crt").select(COLS_CRT, { count: "exact" }).order("id"));
}

/* El pañol: el catálogo con la cantidad total (la pantalla Stock, ~1.800
   artículos) y los depósitos con lo que tiene cada uno (Depósitos, 10 filas
   con la lista adentro). */
export function stock() {
  return traerTodas(() =>
    supabase.from("stock").select("id, codigo, descripcion, cantidad, unidad", { count: "exact" }).order("id"),
  );
}
export function depositos() {
  return traerTodas(() => supabase.from("depositos").select("id, nombre, stock", { count: "exact" }).order("id"));
}

/* Dónde está cada unidad con satelital, para HUB.UBICACION: GET
   {FLOTABOT}/api/ubicaciones, con el mismo texto de lugar que el Excel de
   Arribos de Satelital. FlotaBot lo guarda 60 s: muchas planillas abiertas
   son una sola consulta al satelital por minuto.

   LA SESIÓN DE FLOTABOT, UNA VEZ. Pedir el token cada minuto sería un canje
   contra Supabase por recálculo: la sesión se abre una vez por usuario del hub
   (y otra si cambia quién entró) y después basta la cookie. Si FlotaBot la
   perdió (se reinició, venció) contesta 401: se abre de nuevo y se pide UNA
   vez más; si vuelve a fallar, se tira.

   Lo que vuelve, normalizado para que la fórmula sólo tenga que buscar:
     { actualizado, unidades: { "740": { lugar, hora, estado } },
       patentes: { "KDV538": "740" } }
   con los internos sin ceros adelante y las patentes sin espacios ni guiones,
   aunque FlotaBot las mande de otra forma. */
let sesionDe = null; // el usuario del hub con el que se abrió la sesión en FlotaBot

async function abrirSesion() {
  const s = await abrirSesionEnFlotaBot();
  if (!s.ok) {
    throw new Error(s.motivo === "sin-sesion"
      ? "se cerró la sesión; entrá de nuevo"
      : "no se pudo abrir la sesión en FlotaBot (puede estar reiniciando)");
  }
}

async function pedirUbicaciones() {
  try {
    return await fetch(`${FLOTABOT}/api/ubicaciones`, { method: "GET", credentials: "include", cache: "no-store" });
  } catch {
    throw new Error("no se pudo conectar con FlotaBot");
  }
}

const internoLimpio = (v) => String(v ?? "").trim().replace(/^0+/, "");
const patenteLimpia = (v) => String(v ?? "").toUpperCase().replace(/[^A-Z0-9]/g, "");

export async function ubicaciones() {
  const { data } = await supabase.auth.getSession();
  const yo = data?.session?.user?.id || null;
  if (!yo) throw new Error("se cerró la sesión; entrá de nuevo");
  if (sesionDe !== yo) {
    await abrirSesion();
    sesionDe = yo;
  }
  let r = await pedirUbicaciones();
  if (r.status === 401) {
    sesionDe = null;
    await abrirSesion();
    sesionDe = yo;
    r = await pedirUbicaciones();
  }
  if (r.status === 401) throw new Error("FlotaBot no reconoció la sesión; recargá la página");
  if (r.status === 403) throw new Error("FlotaBot no te deja ver las ubicaciones");
  if (!r.ok) throw new Error(`FlotaBot contestó ${r.status}`);
  let d;
  try {
    d = await r.json();
  } catch {
    throw new Error("FlotaBot contestó algo que no se puede leer");
  }
  if (!d || d.ok !== true || !d.unidades || typeof d.unidades !== "object") {
    throw new Error((d && d.error) || "FlotaBot contestó algo que no se puede leer");
  }
  const unidades = {};
  for (const [k, u] of Object.entries(d.unidades)) {
    const n = internoLimpio(k);
    if (n && u && typeof u === "object") unidades[n] = u;
  }
  const patentes = {};
  for (const [p, interno] of Object.entries(d.patentes || {})) {
    const pl = patenteLimpia(p);
    const n = internoLimpio(interno);
    if (pl && n) patentes[pl] = n;
  }
  return { actualizado: d.actualizado || null, unidades, patentes };
}
